import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { Spin, Alert, Segmented, Empty } from "antd";
import {
  PeopleAltOutlined,
  WhatshotOutlined,
  CheckCircleOutlined,
  RocketLaunchOutlined,
  ShoppingBagOutlined,
  AttachMoneyOutlined,
  Inventory2Outlined,
  WarningAmberOutlined,
  RefreshOutlined,
  CloudDoneOutlined,
  CloudOffOutlined,
  Instagram,
  EmojiEventsOutlined,
  ScheduleOutlined,
  TrendingUpOutlined,
  ArrowForwardIosOutlined,
  ErrorOutlineOutlined,
} from "@mui/icons-material";
import { ResponsiveContainer, FunnelChart, Funnel, LabelList, Tooltip as RTooltip, Cell } from "recharts";
import customClientAxios from "../customAxios";
import { AdminCrmDashboard } from "../utils/ApiEndpoints";
import { fmtRupees } from "../PlatformAnalytics/paUtils";

// ─────────────────────────────────────────────────────────────────────────────
// Bento Grid CRM Dashboard
//
// IA — top-to-bottom narrative:
//   1. Header + window control
//   2. Alert Ribbon (conditional — only renders when ops attention needed)
//   3. Row 1 · Hero KPIs (4 unique metrics, no overlap with funnel below)
//   4. Row 2 · Funnel (2×2 hero) + Activation Phases (1×2 sidekick)
//   5. Row 3 · Revenue (2×1) + Sources (1×1) + Ops Health (1×1)
//   6. Row 4 · Top Nurture Messages (2×1) + Stuck at Stage (1×1)
//
// Style — Bento: 4-col CSS Grid, varied tile spans for hierarchy, soft shadows,
// 20px radius, neutral #F8FAFC page bg. Existing Outfit font + stage colors.
// ─────────────────────────────────────────────────────────────────────────────

const STAGE_PALETTE = {
  prospect:  { color: "#94a3b8", fill: "#cbd5e1", label: "Prospect" },
  qualified: { color: "#a855f7", fill: "#c084fc", label: "Qualified" },
  onboarded: { color: "#3b82f6", fill: "#60a5fa", label: "Onboarded" },
  activated: { color: "#f59e0b", fill: "#fbbf24", label: "Activated" },
  purchased: { color: "#059669", fill: "#10b981", label: "Purchased" },
};

const PHASE_PALETTE = {
  brand_page:    { label: "Brand Page",    color: "#3b82f6", icon: <CheckCircleOutlined /> },
  event_created: { label: "Event Created", color: "#8b5cf6", icon: <RocketLaunchOutlined /> },
  first_sale:    { label: "First Sale",    color: "#f59e0b", icon: <ShoppingBagOutlined /> },
};

// Map a funnel stage to the destination board.
const STAGE_ROUTE = {
  prospect:  "/crm/pre-onboard",
  qualified: "/crm/pre-onboard",
  onboarded: "/crm/post-onboard",
  activated: "/crm/post-onboard",
  purchased: "/crm/post-onboard",
};

// ── Animations ─────────────────────────────────────────────────────────────

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const fadeUp = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); }`;

// ── Layout primitives ──────────────────────────────────────────────────────

const Page = styled.div`
  padding: 24px 28px 64px;
  background: #f8fafc;
  min-height: 100vh;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  h1 {
    font-size: 26px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px;
    letter-spacing: -0.5px;
  }
  p {
    margin: 0;
    color: #64748b;
    font-size: 13px;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #475569;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover { border-color: #cbd5e1; color: #1e293b; background: #f8fafc; }
  &:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }
  svg { font-size: 16px; }
`;

// Bento grid: 4 columns desktop → 2 → 1 mobile.
// Children use $col/$row to declare their span. Defaults to 1×1.
const Bento = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: minmax(120px, auto);
  gap: 14px;
  animation: ${fadeUp} 0.35s ease both;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

// Single Tile primitive. Spans, padding, optional clickable behaviour.
const Tile = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: ${(p) => p.$pad || "18px 20px"};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  grid-column: span ${(p) => p.$col || 1};
  grid-row: span ${(p) => p.$row || 1};

  @media (max-width: 1280px) {
    grid-column: span ${(p) => Math.min(p.$col || 1, 2)};
  }
  @media (max-width: 720px) {
    grid-column: span 1;
  }

  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;
      &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 6px 20px rgba(15, 23, 42, 0.07);
        transform: translateY(-1px);
      }
      &:focus-visible {
        outline: 2px solid #6366f1;
        outline-offset: 2px;
      }
    `}
`;

const TileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.2px;
  }
  .sub {
    font-size: 11.5px;
    color: #94a3b8;
  }
`;

// ── Alert ribbon ───────────────────────────────────────────────────────────

const AlertBanner = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin-bottom: 16px;

  @media (min-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
`;

const AlertItem = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid ${(p) => p.$tone === "danger" ? "rgba(239,68,68,0.3)" : p.$tone === "warn" ? "rgba(245,158,11,0.3)" : "rgba(99,102,241,0.25)"};
  background: ${(p) => p.$tone === "danger" ? "rgba(239,68,68,0.06)" : p.$tone === "warn" ? "rgba(245,158,11,0.06)" : "rgba(99,102,241,0.05)"};
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(15,23,42,0.06); }
  &:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }

  .icon {
    width: 34px; height: 34px; border-radius: 10px;
    background: ${(p) => p.$tone === "danger" ? "rgba(239,68,68,0.15)" : p.$tone === "warn" ? "rgba(245,158,11,0.15)" : "rgba(99,102,241,0.15)"};
    color: ${(p) => p.$tone === "danger" ? "#dc2626" : p.$tone === "warn" ? "#d97706" : "#6366f1"};
    display: flex; align-items: center; justify-content: center;
    svg { font-size: 18px; }
  }
  .body {
    display: flex; flex-direction: column; gap: 2px; text-align: left;
  }
  .title {
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => p.$tone === "danger" ? "#991b1b" : p.$tone === "warn" ? "#92400e" : "#3730a3"};
    line-height: 1.2;
  }
  .meta { font-size: 11.5px; color: #64748b; }
`;

// ── KPI tiles (Row 1) ──────────────────────────────────────────────────────

const KpiLabel = styled.div`
  font-size: 11.5px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const KpiIcon = styled.div`
  width: 36px; height: 36px;
  border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  svg { font-size: 20px; }
`;

const KpiValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.05;
  letter-spacing: -0.6px;
  margin-top: 4px;
`;

const KpiFoot = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// ── Funnel (Row 2 hero) ────────────────────────────────────────────────────

const FunnelWrap = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 18px;
  flex: 1;
  align-items: stretch;
  min-height: 240px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const StageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StageRow = styled.button`
  all: unset;
  display: grid;
  grid-template-columns: 12px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 9px;
  font-size: 12.5px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #f8fafc; }
  &:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }

  .dot {
    width: 10px; height: 10px; border-radius: 3px;
    background: ${(p) => p.$color};
  }
  .label {
    color: #1e293b;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .count {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }
`;

const ConvStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
`;

const ConvPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: ${(p) => (p.$good ? "rgba(5,150,105,0.08)" : p.$warn ? "rgba(245,158,11,0.08)" : "#f1f5f9")};
  border: 1px solid ${(p) => (p.$good ? "rgba(5,150,105,0.2)" : p.$warn ? "rgba(245,158,11,0.2)" : "#e2e8f0")};
  font-size: 11px;
  color: #475569;
  font-weight: 500;

  strong {
    color: ${(p) => (p.$good ? "#047857" : p.$warn ? "#b45309" : "#0f172a")};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`;

// ── Activation Phases (Row 2 sidekick) ─────────────────────────────────────

const PhaseStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const PhaseCard = styled.div`
  position: relative;
  padding: 14px 14px;
  border-radius: 14px;
  background: ${(p) => `linear-gradient(135deg, ${p.$color}10, ${p.$color}05)`};
  border: 1px solid ${(p) => `${p.$color}30`};

  .hd { display: flex; align-items: center; justify-content: space-between; }
  .lbl {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    color: ${(p) => p.$color};
    display: flex; align-items: center; gap: 6px;
    svg { font-size: 14px; }
  }
  .count {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1;
    margin-top: 6px;
    font-variant-numeric: tabular-nums;
  }
  .sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 4px;
  }
`;

// ── Revenue (Row 3) ────────────────────────────────────────────────────────

const RevenueLayout = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 14px;
  flex: 1;

  @media (max-width: 960px) { grid-template-columns: 1fr; }
`;

const RevenueHero = styled.div`
  padding: 18px 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(16,185,129,0.1), rgba(99,102,241,0.06));
  border: 1px solid rgba(16,185,129,0.25);
  display: flex;
  flex-direction: column;
  justify-content: center;

  .lbl {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    color: #047857;
  }
  .val {
    font-size: 30px;
    font-weight: 700;
    color: #064e3b;
    line-height: 1.05;
    margin-top: 6px;
    letter-spacing: -0.6px;
    font-variant-numeric: tabular-nums;
  }
  .sub {
    font-size: 11.5px;
    color: #047857aa;
    margin-top: 4px;
  }
`;

const RevenueRows = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const RevenueCell = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;

  .lbl {
    font-size: 10.5px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 600;
  }
  .val {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
  }
  .sub {
    font-size: 10.5px;
    color: #94a3b8;
    margin-top: 2px;
  }
`;

// ── Sources (Row 3) ────────────────────────────────────────────────────────

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const SourceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px;
  align-items: center;

  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    grid-column: 1 / -1;
    font-size: 12px;
  }
  .name {
    color: #475569;
    font-weight: 500;
    text-transform: capitalize;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .count {
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }
  .bar {
    grid-column: 1 / -1;
    height: 6px;
    background: #f1f5f9;
    border-radius: 3px;
    overflow: hidden;
    div {
      height: 100%;
      width: ${(p) => p.$pct}%;
      background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
      border-radius: 3px;
      transition: width 0.5s ease;
    }
  }
`;

// ── Ops Health (Row 3) ─────────────────────────────────────────────────────

const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  flex: 1;
`;

const HealthCell = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: ${(p) => p.$danger ? "rgba(239,68,68,0.07)" : p.$warn ? "rgba(245,158,11,0.07)" : "#f8fafc"};
  border: 1px solid ${(p) => p.$danger ? "rgba(239,68,68,0.25)" : p.$warn ? "rgba(245,158,11,0.25)" : "#e2e8f0"};

  .h {
    font-size: 10.5px;
    color: ${(p) => p.$danger ? "#b91c1c" : p.$warn ? "#b45309" : "#64748b"};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 700;
    display: flex; align-items: center; gap: 5px;
    svg { font-size: 13px; }
  }
  .v {
    font-size: 18px;
    font-weight: 700;
    color: ${(p) => p.$danger ? "#991b1b" : p.$warn ? "#92400e" : "#0f172a"};
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
  }
  .s {
    font-size: 10.5px;
    color: #94a3b8;
    margin-top: 2px;
  }
`;

// ── Top Messages / Stuck (Row 4) ───────────────────────────────────────────

const MsgRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 50px 1fr;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
  background: white;
  transition: background 0.15s, border-color 0.15s;
  & + & { margin-top: 6px; }
  &:hover { background: #f8fafc; border-color: #e2e8f0; }
`;

const MsgKey = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  text-transform: capitalize;
`;

const MsgStep = styled.div`
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(99,102,241,0.1);
  color: #6366f1;
  font-weight: 700;
  text-align: center;
  justify-self: center;
`;

const MsgStats = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const MsgStat = styled.span`
  padding: 2px 7px;
  border-radius: 5px;
  background: ${(p) => p.$bg || "#f1f5f9"};
  color: ${(p) => p.$fg || "#64748b"};
  font-weight: 600;
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
`;

const StuckRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
  background: white;
  & + & { margin-top: 6px; }
  &:hover { background: #f8fafc; }

  .left {
    display: flex; flex-direction: column; gap: 2px;
    .stage {
      font-size: 12px;
      font-weight: 600;
      color: #1e293b;
      text-transform: capitalize;
    }
    .phase { font-size: 10.5px; color: #94a3b8; }
  }
  .right {
    text-align: right;
    .count {
      font-size: 16px;
      font-weight: 700;
      color: #b45309;
      font-variant-numeric: tabular-nums;
    }
    .days { font-size: 10.5px; color: #94a3b8; }
  }
`;

// ── Helpers ────────────────────────────────────────────────────────────────

// Revenue API values are in PAISE (see SaleTransaction.paymentBreakdown etc.).
// fmtRupees converts to rupees and short-scales (K / L / Cr).
const inr = fmtRupees;
const num = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// ── Component ──────────────────────────────────────────────────────────────

const CrmDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [windowKey, setWindowKey] = useState("30d");

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const r = await customClientAxios().get(
        `${AdminCrmDashboard}?window=${windowKey}&stuckThresholdDays=30`,
      );
      setData(r?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowKey]);

  useEffect(() => { load(true); }, [load]);

  // Funnel data for recharts. Order matters — Recharts narrows top→bottom.
  const funnelData = useMemo(() => {
    if (!data) return [];
    const f = data.funnel;
    return ["prospect", "qualified", "onboarded", "activated", "purchased"].map((k) => ({
      key: k,
      name: STAGE_PALETTE[k].label,
      value: f[k] || 0,
      fill: STAGE_PALETTE[k].fill,
    }));
  }, [data]);

  const totalInFunnel = useMemo(() => {
    if (!data) return 0;
    const f = data.funnel;
    return (f.prospect || 0) + (f.qualified || 0) + (f.onboarded || 0) + (f.activated || 0) + (f.purchased || 0) + (f.lost || 0);
  }, [data]);

  const overallConvRate = useMemo(() => {
    if (!data || !totalInFunnel) return 0;
    return (data.funnel.purchased / totalInFunnel) * 100;
  }, [data, totalInFunnel]);

  const alerts = useMemo(() => {
    if (!data) return [];
    const out = [];
    if (data.ops?.outboxFailed > 0) {
      out.push({
        tone: "danger",
        icon: <ErrorOutlineOutlined />,
        title: `${data.ops.outboxFailed} nurture message${data.ops.outboxFailed === 1 ? "" : "s"} failed`,
        meta: "Outbox queue · investigate sender or template",
        action: () => navigate("/nurture/messages"),
      });
    }
    if (data.hubspot?.errored > 0) {
      out.push({
        tone: "warn",
        icon: <CloudOffOutlined />,
        title: `${data.hubspot.errored} contacts failed HubSpot sync`,
        meta: "Sync errors blocking CRM mirroring",
        action: () => navigate("/analytics/leads"),
      });
    }
    const stuckTotal = (data.stuckAtStage || []).reduce((sum, s) => sum + (s.count || 0), 0);
    if (stuckTotal >= 50) {
      out.push({
        tone: "info",
        icon: <ScheduleOutlined />,
        title: `${stuckTotal} contacts idle 30+ days`,
        meta: "Consider a re-engagement nurture",
        action: () => navigate("/crm/pre-onboard"),
      });
    }
    return out;
  }, [data, navigate]);

  if (loading) {
    return (
      <Page>
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#64748b", fontSize: 13 }}>Loading CRM dashboard…</div>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Alert type="error" message="Failed to load dashboard" description={error} showIcon />
      </Page>
    );
  }

  if (!data) return null;

  const {
    funnel,
    conversionRates,
    sources,
    revenue,
    instagram,
    hubspot,
    ops,
    topMessages,
    stuckAtStage,
    totalLeads,
  } = data;

  const phases = funnel.activated_phases || {};
  const purchasedCount = funnel.purchased || 0;
  const phaseCumulative = {
    brand_page:    (phases.brand_page || 0) + (phases.event_created || 0) + (phases.first_sale || 0) + purchasedCount,
    event_created: (phases.event_created || 0) + (phases.first_sale || 0) + purchasedCount,
    first_sale:    (phases.first_sale || 0) + purchasedCount,
  };

  const goStage = (stageKey) => navigate(STAGE_ROUTE[stageKey] || "/crm/pre-onboard");

  return (
    <Page>
      <Header>
        <div>
          <h1>CRM Dashboard</h1>
          <p>Every contact, every stage — funnel, conversion, revenue, and ops health at a glance.</p>
        </div>
        <RightControls>
          <Segmented
            value={windowKey}
            onChange={(v) => setWindowKey(v)}
            options={[
              { label: "7d", value: "7d" },
              { label: "30d", value: "30d" },
              { label: "90d", value: "90d" },
              { label: "From First", value: "from_first" },
              { label: "All", value: "all" },
            ]}
          />
          <RefreshBtn onClick={() => load(false)} disabled={refreshing} aria-label="Refresh dashboard">
            <RefreshOutlined style={{ animation: refreshing ? `${spin} 1s linear infinite` : "none" }} />
            Refresh
          </RefreshBtn>
        </RightControls>
      </Header>

      {/* ── Alert ribbon — conditional ─────────────────────────────────── */}
      {alerts.length > 0 && (
        <AlertBanner>
          {alerts.map((a, i) => (
            <AlertItem key={i} $tone={a.tone} onClick={a.action} type="button">
              <div className="icon">{a.icon}</div>
              <div className="body">
                <div className="title">{a.title}</div>
                <div className="meta">{a.meta}</div>
              </div>
            </AlertItem>
          ))}
        </AlertBanner>
      )}

      {/* ── Bento ──────────────────────────────────────────────────────── */}
      <Bento>
        {/* Row 1 · 4 hero KPIs */}
        <Tile>
          <TileHeader><KpiLabel>Total Contacts</KpiLabel><KpiIcon $bg="rgba(99,102,241,0.12)" $fg="#6366f1"><PeopleAltOutlined /></KpiIcon></TileHeader>
          <KpiValue>{num(totalLeads)}</KpiValue>
          <KpiFoot>All-time across every stage</KpiFoot>
        </Tile>

        <Tile>
          <TileHeader><KpiLabel>Plan Purchases</KpiLabel><KpiIcon $bg="rgba(5,150,105,0.12)" $fg="#059669"><EmojiEventsOutlined /></KpiIcon></TileHeader>
          <KpiValue>{num(purchasedCount)}</KpiValue>
          <KpiFoot>Paying customers in window</KpiFoot>
        </Tile>

        <Tile>
          <TileHeader><KpiLabel>Total Revenue</KpiLabel><KpiIcon $bg="rgba(16,185,129,0.12)" $fg="#10b981"><AttachMoneyOutlined /></KpiIcon></TileHeader>
          <KpiValue>{inr(revenue.totalGrowezyRevenue)}</KpiValue>
          <KpiFoot>Platform fee + subscriptions</KpiFoot>
        </Tile>

        <Tile>
          <TileHeader><KpiLabel>Conversion Rate</KpiLabel><KpiIcon $bg="rgba(245,158,11,0.12)" $fg="#f59e0b"><TrendingUpOutlined /></KpiIcon></TileHeader>
          <KpiValue>{overallConvRate.toFixed(1)}%</KpiValue>
          <KpiFoot>Prospect → Plan Purchased</KpiFoot>
        </Tile>

        {/* Row 2 · Funnel hero (3×2) + Activation Phases (1×2) */}
        <Tile $col={3} $row={2}>
          <TileHeader>
            <h3>Funnel — Prospect to Plan Purchase</h3>
            <span className="sub">Click any stage to drill into the board</span>
          </TileHeader>

          <FunnelWrap>
            <StageList>
              {funnelData.map((d) => (
                <StageRow
                  key={d.key}
                  $color={STAGE_PALETTE[d.key].color}
                  onClick={() => goStage(d.key)}
                  type="button"
                  aria-label={`View ${d.name} contacts`}
                >
                  <div className="dot" />
                  <div className="label">
                    {d.name}
                    <ArrowForwardIosOutlined style={{ fontSize: 10, color: "#cbd5e1" }} />
                  </div>
                  <div className="count">{num(d.value)}</div>
                </StageRow>
              ))}
              {funnel.lost > 0 && (
                <StageRow $color="#ef4444" type="button" disabled>
                  <div className="dot" />
                  <div className="label" style={{ color: "#b91c1c" }}>Lost</div>
                  <div className="count" style={{ color: "#b91c1c" }}>{num(funnel.lost)}</div>
                </StageRow>
              )}
            </StageList>

            <div style={{ minHeight: 220, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                <FunnelChart>
                  <RTooltip
                    cursor={false}
                    contentStyle={{
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                      padding: "8px 12px",
                    }}
                    formatter={(value, _name, props) => [`${num(value)} contacts`, props?.payload?.name]}
                  />
                  <Funnel
                    dataKey="value"
                    data={funnelData}
                    isAnimationActive
                    onClick={(payload) => payload?.key && goStage(payload.key)}
                    cursor="pointer"
                  >
                    <LabelList
                      position="right"
                      fill="#0f172a"
                      stroke="none"
                      dataKey="name"
                      fontSize={11.5}
                      fontWeight={600}
                    />
                    <LabelList
                      position="center"
                      fill="white"
                      stroke="none"
                      dataKey="value"
                      fontSize={13}
                      fontWeight={700}
                    />
                    {funnelData.map((entry) => (
                      <Cell key={entry.key} fill={entry.fill} />
                    ))}
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            </div>
          </FunnelWrap>

          <ConvStrip>
            {[
              ["Prospect → Qualified", conversionRates.prospectToQualified],
              ["Qualified → Onboarded", conversionRates.qualifiedToOnboarded],
              ["Onboarded → Activated", conversionRates.onboardedToActivated],
              ["Activated → Purchased", conversionRates.activatedToPurchased],
            ].map(([label, pct]) => (
              <ConvPill key={label} $good={pct >= 30} $warn={pct > 0 && pct < 15}>
                {label} <strong>{pct}%</strong>
              </ConvPill>
            ))}
          </ConvStrip>
        </Tile>

        <Tile $col={1} $row={2}>
          <TileHeader>
            <h3>Activation Phases</h3>
            <span className="sub">Unique owners reached</span>
          </TileHeader>
          <PhaseStack>
            {["brand_page", "event_created", "first_sale"].map((p) => {
              const vis = PHASE_PALETTE[p];
              const currentHere = phases[p] || 0;
              const everReached = phaseCumulative[p];
              return (
                <PhaseCard key={p} $color={vis.color}>
                  <div className="hd">
                    <div className="lbl">{vis.icon}{vis.label}</div>
                  </div>
                  <div className="count">{num(everReached)}</div>
                  <div className="sub">
                    {num(currentHere)} currently here
                    {everReached > currentHere && ` · ${num(everReached - currentHere)} moved on`}
                  </div>
                </PhaseCard>
              );
            })}
          </PhaseStack>
        </Tile>

        {/* Row 3 · Revenue (2×1) + Sources (1×1) + Ops Health (1×1) */}
        <Tile $col={2}>
          <TileHeader>
            <h3>Revenue</h3>
            <span className="sub">Window: {windowKey}</span>
          </TileHeader>
          <RevenueLayout>
            <RevenueHero>
              <div className="lbl">Total Growezy Revenue</div>
              <div className="val">{inr(revenue.totalGrowezyRevenue)}</div>
              <div className="sub">Platform fee + subscription payments</div>
            </RevenueHero>
            <RevenueRows>
              <RevenueCell>
                <div className="lbl">Gross Sales</div>
                <div className="val">{inr(revenue.grossSales)}</div>
                <div className="sub">{num(revenue.transactions)} txns</div>
              </RevenueCell>
              <RevenueCell>
                <div className="lbl">Platform Fee</div>
                <div className="val">{inr(revenue.platformFee)}</div>
              </RevenueCell>
              <RevenueCell>
                <div className="lbl">Creator Payouts</div>
                <div className="val">{inr(revenue.creatorPayouts)}</div>
              </RevenueCell>
              <RevenueCell>
                <div className="lbl">Subscriptions</div>
                <div className="val">{inr(revenue.subscriptionsPaid)}</div>
              </RevenueCell>
            </RevenueRows>
          </RevenueLayout>
        </Tile>

        <Tile>
          <TileHeader>
            <h3>Onboarded Sources</h3>
            <span className="sub">Top channels</span>
          </TileHeader>
          {sources.onboarded.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 12, color: "#94a3b8" }}>No onboarded contacts yet</span>} />
          ) : (
            <SourceList>
              {sources.onboarded.slice(0, 5).map((s) => (
                <SourceRow key={s.source} $pct={s.percent}>
                  <div className="top">
                    <div className="name">
                      {s.source === "instagram" && <Instagram style={{ fontSize: 14, color: "#e11d48" }} />}
                      {s.source}
                    </div>
                    <div className="count">{num(s.count)}</div>
                  </div>
                  <div className="bar"><div /></div>
                </SourceRow>
              ))}
            </SourceList>
          )}
        </Tile>

        <Tile>
          <TileHeader>
            <h3>Ops Health</h3>
            <span className="sub">Sync · Queue · IG</span>
          </TileHeader>
          <HealthGrid>
            <HealthCell>
              <div className="h"><CloudDoneOutlined /> HubSpot synced</div>
              <div className="v">{num(hubspot.synced)}</div>
              <div className="s">of {num(totalLeads)} total</div>
            </HealthCell>
            <HealthCell $warn={hubspot.unsynced > 0}>
              <div className="h"><CloudOffOutlined /> Unsynced</div>
              <div className="v">{num(hubspot.unsynced)}</div>
              {hubspot.errored > 0 && <div className="s">{hubspot.errored} errored</div>}
            </HealthCell>
            <HealthCell $danger={ops.outboxFailed > 0} $warn={ops.outboxPending > 0}>
              <div className="h"><Inventory2Outlined /> Outbox queue</div>
              <div className="v">{num(ops.outboxPending)}</div>
              <div className="s">
                {ops.outboxFailed > 0 ? `${ops.outboxFailed} failed · ` : ""}
                lag {Math.round((ops.outboxLagSec || 0) / 60)}m
              </div>
            </HealthCell>
            <HealthCell>
              <div className="h"><Instagram style={{ fontSize: 13 }} /> IG linked</div>
              <div className="v">{num(instagram.linked)}</div>
              <div className="s">Cross-sell targets</div>
            </HealthCell>
          </HealthGrid>
        </Tile>

        {/* Row 4 · Top Messages (3×1) + Stuck (1×1) */}
        <Tile $col={3}>
          <TileHeader>
            <h3>Top Performing Nurture Messages</h3>
            <span className="sub">Last-touch attribution · {windowKey}</span>
          </TileHeader>
          {topMessages.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 12, color: "#94a3b8" }}>No attributed transitions yet</span>} />
          ) : (
            <div>
              {topMessages.map((m) => (
                <MsgRow key={`${m.sequenceKey}-${m.stepDay}`}>
                  <MsgKey>{m.sequenceKey.replace(/_/g, " ")}</MsgKey>
                  <MsgStep>D{m.stepDay}</MsgStep>
                  <MsgStats>
                    {m.qualified > 0 && <MsgStat $bg="rgba(168,85,247,0.12)" $fg="#7c3aed">{m.qualified} qual</MsgStat>}
                    {m.onboarded > 0 && <MsgStat $bg="rgba(59,130,246,0.12)" $fg="#2563eb">{m.onboarded} onb</MsgStat>}
                    {m.activated > 0 && <MsgStat $bg="rgba(245,158,11,0.12)" $fg="#d97706">{m.activated} act</MsgStat>}
                    {m.purchased > 0 && <MsgStat $bg="rgba(5,150,105,0.12)" $fg="#047857">{m.purchased} purch</MsgStat>}
                    <MsgStat>{m.totalAttributions} total</MsgStat>
                  </MsgStats>
                </MsgRow>
              ))}
            </div>
          )}
        </Tile>

        <Tile>
          <TileHeader>
            <h3>Stuck at Stage</h3>
            <span className="sub"><WarningAmberOutlined style={{ fontSize: 13, verticalAlign: -2 }} /> {">"} 30 days idle</span>
          </TileHeader>
          {stuckAtStage.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 12, color: "#94a3b8" }}>No stuck contacts</span>} />
          ) : (
            <div>
              {stuckAtStage.map((s, i) => (
                <StuckRow key={`${s.stage}-${s.activationPhase || "none"}-${i}`}>
                  <div className="left">
                    <div className="stage">{titleCase(s.stage)}</div>
                    {s.activationPhase && <div className="phase">phase · {s.activationPhase.replace(/_/g, " ")}</div>}
                  </div>
                  <div className="right">
                    <div className="count">{num(s.count)}</div>
                    <div className="days">avg {s.avgDaysSinceUpdate}d idle</div>
                  </div>
                </StuckRow>
              ))}
            </div>
          )}
        </Tile>
      </Bento>
    </Page>
  );
};

export default CrmDashboard;
